import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface RowAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface RowActionsMenuProps {
  actions: RowAction[];
}

interface MenuCoords {
  top: number;
  left: number;
  minWidth: number;
}

export function RowActionsMenu({ actions }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Flag ref: evita che il mousedown di apertura chiuda subito il menu
  const justOpenedRef = useRef(false);

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current!.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 4,
      left: rect.right - 160,
      minWidth: 160,
    });
    justOpenedRef.current = true;
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Ignora il pointerdown che ha aperto il menu
      if (justOpenedRef.current) {
        justOpenedRef.current = false;
        return;
      }
      if (
        triggerRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.right - 160, minWidth: 160 });
    };

    // Usa capture:true su pointerdown per intercettarlo prima di qualsiasi handler React
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  return (
    <>
      <div style={{ display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
        <button
          ref={triggerRef}
          onClick={openMenu}
          aria-label="Azioni"
          aria-expanded={open}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.25rem',
            borderRadius: 'var(--radius-interactive)',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--color-text-faint)',
            transition: 'color var(--transition-interactive)',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-text)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-faint)')}
        >
          <MoreHorizontal size={16} />
        </button>
      </div>

      {open && coords && createPortal(
        <div
          ref={menuRef}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: 9999,
            background: 'var(--glass-bg-elevated)',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 'var(--radius-card)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            minWidth: coords.minWidth,
            overflow: 'hidden',
          }}
        >
          {actions.map((action, i) => (
            <button
              key={i}
              disabled={action.disabled}
              onClick={e => { action.onClick(e); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.5rem 0.875rem',
                background: 'none',
                border: 'none',
                cursor: action.disabled ? 'not-allowed' : 'pointer',
                fontSize: 'var(--text-sm)',
                color: action.variant === 'danger'
                  ? 'var(--color-error)'
                  : action.disabled
                    ? 'var(--color-text-faint)'
                    : 'var(--color-text)',
                textAlign: 'left',
                transition: 'background var(--transition-interactive)',
                opacity: action.disabled ? 0.5 : 1,
              }}
              onMouseEnter={e => { if (!action.disabled) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              {action.icon && <span style={{ flexShrink: 0, display: 'flex' }}>{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
